// @ts-nocheck
import {
    AdminRoleId,
    AgGridFilterType,
    ClientWalletRolesMap,
    FilterOption,
    getCountryOptions,
    getStateProvinceOptions
} from '@tassat/common-definitions';
import { APIConnectionFirmsRecord, FirmAssetBalanceStatistics } from '@tassat/fred-definitions';
import { FirmStatsMapped } from '@tassat/fred-extended-definitions';
import { formatAsAsset, formatAsAssetNoSign, multiplySafe } from '@tassat/utility';

import { download } from 'Lib/exportHelper';
import { AssetBalanceColumnDef, buildAssetBalanceColumnDefs, getAssetBalanceColumnValues } from 'Lib/exports/assetBalanceColumnsHelper';
import { caseInsensitiveSort, formatEpochDate, obscureAccountNo } from 'Lib/stringHelpers';
import AdminStore from 'Reducers/store/adminStore';
import { getLinkedAccountForWallet } from 'Selectors/accountRelationships';
import { getAllBDAccountsByFirmId, getAllMappedWalletsByFirmId, getAllWalletsByFirmId } from 'Selectors/accounts';
import { getApiConnectionFirmsArray } from 'Selectors/apiConnectionFirms';
import { getAllAssetsByCurCodeAndPriceArray } from 'Selectors/assets';
import { getCurrentRole, getUsers } from 'Selectors/common';
import { getFirmAssetEntitlementsByFirmId } from 'Selectors/firmAssetEntitlements';
import { getAPIFirmsArray, getFirmByFirmId, getFirmsWithStatsArray } from 'Selectors/firms';
import { getMappedUserAccountsByUserId } from 'Selectors/userAccounts';
import { getUserById, getUsersByFirmId } from 'Selectors/users';
import { IFeaturesContext } from 'SharedComponents/hooks/useFeatures';

export const exportApiFirmData = (filter: FilterOption, applicationName: string) => {
    const store = AdminStore.getState();
    let firms = getAPIFirmsArray(store, undefined, filter && filter.type === AgGridFilterType.Equal && filter.colId === 'IsClosed' && filter.filter === 'true');
    const apiFirms = getApiConnectionFirmsArray(store);

    if (filter && filter.type === AgGridFilterType.Equal) {
        firms = firms.filter(mappedFirm => String(mappedFirm[filter.colId]) === filter.filter);
    }

    const apiConnections = store.apiConnections;
    let exportString = 'API Connection Name,API Connection Locked Status,API Connection Notification Email,Firm Name\n';

    firms.forEach((record: FirmStatsMapped) => {
        const firm = record;
        const linkedConnections = apiFirms.filter((apiFirm: APIConnectionFirmsRecord) => apiFirm.FirmId === firm.FirmId);
        if (linkedConnections && linkedConnections.length > 0) {
            linkedConnections.forEach(apiFirm => {
                const api = apiConnections[apiFirm.APIConnectionId];
                if (!api) return;
                exportString += `"${api.APIConnectionName}","${api.IsLocked}","${api.APIFirmEmail}","${firm.Name}"\n`;
            });
        } else {
            exportString += `"","","","${firm.Name}"\n`;
        }
    });

    download('Lynq_Platform_API_Firms.csv', exportString);
};

export const exportManageFirmData = (filter: FilterOption, features?: IFeaturesContext): void => {
    const state = AdminStore.getState();
    let firms = getFirmsWithStatsArray(state, undefined, filter && filter.type === AgGridFilterType.Equal && filter.colId === 'IsClosed' && filter.filter === 'true');
    const currencyGroups = getAllAssetsByCurCodeAndPriceArray(state);

    if (filter && filter.type === AgGridFilterType.Equal) {
        firms = firms.filter(mappedFirm => String(mappedFirm.firm[filter.colId]) === filter.filter);
    }

    const assetBalancesColumnDefs: AssetBalanceColumnDef[] = buildAssetBalanceColumnDefs(currencyGroups);

    const assetBalancesColumnsHeader = assetBalancesColumnDefs
        .map(({ header }) => header)
        .join(',');

    let exportString = `Firm Name,Asset Permissions,${assetBalancesColumnsHeader},Token API Enabled Status,Liquidity Facility Enabled Status,Collateral Management,Closed Status,Locked Status,Frozen Status,Country,State/Province,Firm Email,Notes,Created At,Updated At,Updated By,Closed At,Reopened At\n`;

    firms.forEach((record: FirmStatsMapped) => {
        const createTime = record.firm.CreatedAt && record.firm.CreatedAt !== '0' ? formatEpochDate(record.firm.CreatedAt) : '';
        const updateTime = record.firm.UpdatedAt && record.firm.UpdatedAt !== '0' ? formatEpochDate(record.firm.UpdatedAt) : '';
        const closedAt = record.firm.ClosedAt && record.firm.ClosedAt !== '0' ? formatEpochDate(record.firm.ClosedAt) : '';
        const reopenAt = record.firm.ReopenAt && record.firm.ReopenAt !== '0' ? formatEpochDate(record.firm.ReopenAt) : '';
        const countryName = getCountryOptions()
            .find(({ value }) => value === record.firm.CountryCode)?.label || '';
        const stateProvinceName = getStateProvinceOptions(record.firm.CountryCode)
            .find(({ value }) => value === record.firm.StateCode)?.label || '';
        const modifiedByUser = getUserById(state, record.firm.ModifiedBy);
        const assetPermissions = getFirmAssetEntitlementsByFirmId(state, record.firm.FirmId)
            .map(({ Ticker }) => Ticker)
            .sort(caseInsensitiveSort)
            .join(', ');

        // FirmStatsMapped declares firmAssetStats as a single FirmAssetBalanceStatistics,
        // but it is actually populated keyed by AssetId — see getFirmsWithStatsArray.
        const firmAssetStatsByAssetId = record.firmAssetStats as unknown as Record<string, FirmAssetBalanceStatistics> | undefined;
        const firmAssetBalancesColumnsValues: string[] = assetBalancesColumnDefs
            .map((col: AssetBalanceColumnDef): string => {
                const value: string | number = firmAssetStatsByAssetId?.[col.assetId]?.[col.valuePath] || 0;
                const amount = col.price ? multiplySafe(Number(value), Number(col.price)) : value;

                return col.formatAsAssetNoSign ?
                    `"${formatAsAssetNoSign(amount, col.security)}"`
                    : `"${formatAsAsset(amount, col.ccy, 'symbol')}"`;
            });

        const cellValues = [
            `"${record.firm.Name}"`,
            `"${assetPermissions}"`,
            ...firmAssetBalancesColumnsValues,
            `"${record.firm.IsTokenAPIFirm}"`,
            `"${record.firm.IsLiquidityFacility}"`,
            `"${record.firm.IsCollateral}"`,
            `"${record.firm.IsClosed}"`,
            `"${record.firm.IsLocked}"`,
            `"${record.firm.IsFrozen}"`,
            `"${countryName}"`,
            `"${stateProvinceName}"`,
            `"${record.firm.Email}"`,
            `"${record.firm.Notes}"`,
            `"${createTime}"`,
            `"${updateTime}"`,
            `"${modifiedByUser.Username}"`,
            `"${closedAt}"`,
            `"${reopenAt}"\n`
        ].join(',');

        exportString += cellValues;
    });

    download('Lynq_Platform_Firms.csv', exportString);
};

export const exportFirmWalletData = (firmId: string, areClosedWallets: boolean, features?: IFeaturesContext): void => {
    const state = AdminStore.getState();
    const firm = state.firms[firmId];

    if (!firm) return;
    const accounts = getAllWalletsByFirmId(state, firmId);

    const currencyGroups = getAllAssetsByCurCodeAndPriceArray(state);
    const assetBalancesColumnDefs: AssetBalanceColumnDef[] = buildAssetBalanceColumnDefs(currencyGroups);

    const assetBalancesColumnsHeader = assetBalancesColumnDefs
        .map(({ header }) => header)
        .join(',');

    let exportString = `Account Name,Primary Account,Access Control,Account Address,Linked BD Account Number,Linked BD Account Name,${assetBalancesColumnsHeader},Locked Status,Created At,Updated At,Updated By,Closed At,Reopened At\n`;

    accounts.forEach(account => {
        const assetBalancesColumnsValues = getAssetBalanceColumnValues(state, account.AccountId, assetBalancesColumnDefs);
        const associatedWallet = getLinkedAccountForWallet(state, account.AccountId)[0];
        const modifiedByUser = account.ModifiedBy !== '0' ? getUserById(state, account.ModifiedBy).Username : '';

        const createTime = account.CreatedAt && account.CreatedAt !== '0' ? formatEpochDate(account.CreatedAt) : '';
        const closeTime = account.ClosedAt && account.ClosedAt !== '0' ? formatEpochDate(account.ClosedAt) : '';
        const updateTime = account.UpdatedAt && account.UpdatedAt !== '0' ? formatEpochDate(account.UpdatedAt) : '';
        const reopenTime = account.ReopenAt && account.ReopenAt !== '0' ? formatEpochDate(account.ReopenAt) : '';

        const cellValuesArray = [
            `"${account.AccountName}"`,
            `"${account.IsPrimary}"`,
            `"${account.AccessControl}"`,
            `"${account.Account}"`,
            `"${associatedWallet.Account}"`,
            `"${associatedWallet.AccountName}"`,
            ...assetBalancesColumnsValues,
            `"${account.IsLocked}"`,
            `"${createTime}"`,
            `"${updateTime}"`,
            `"${modifiedByUser}"`,
            `"${closeTime}"`,
            `"${reopenTime}"\n`
        ].join(',');

        exportString += cellValuesArray;
    });

    download('Lynq_Platform_Firm_Accounts.csv', exportString);
};

export const exportFirmAccountData = (firmId: string, features?: IFeaturesContext) => {
    const state = AdminStore.getState();
    const firm = getFirmByFirmId(state, firmId);

    if (!firm) return;
    const bdAccounts = getAllBDAccountsByFirmId(state, firmId);
    const allMappedWallets = getAllMappedWalletsByFirmId(state, firmId);
    let exportString = 'Firm Name,BD Account Name,Account Number,Primary Account,Linked Accounts,Locked Status,Last Activity,Created At,Updated At,Updated By,Closed At,Reopened At\n';

    bdAccounts.forEach(bdAccount => {
        const linkedWallets = allMappedWallets
            .filter((mappedWallet) => mappedWallet.wallet?.LinkedAccount?.AccountId === bdAccount.AccountId);

        const allAccountStats = Object.values(linkedWallets)
            .reduce((acc, { walletStats }) => {
                const stats = Object.values(walletStats);

                return [ ...acc, ...stats ];
            }, []);

        const lastActivityPosition = allAccountStats.sort((a, b) => b.LastTransactionTime - a.LastTransactionTime)[0];

        const lastActivityTime = lastActivityPosition && lastActivityPosition.LastTransactionTime !== '0' ? formatEpochDate(lastActivityPosition.LastTransactionTime) : '';
        const createTime = bdAccount.CreatedAt && bdAccount.CreatedAt !== '0' ? formatEpochDate(bdAccount.CreatedAt) : '';
        const closeTime = bdAccount.ClosedAt && bdAccount.ClosedAt !== '0' ? formatEpochDate(bdAccount.ClosedAt) : '';
        const modifiedByUser = bdAccount.ModifiedBy !== '0' ? getUserById(state, bdAccount.ModifiedBy).Username : '';
        const updateTime = bdAccount.UpdatedAt && bdAccount.UpdatedAt !== '0' ? formatEpochDate(bdAccount.UpdatedAt) : '';
        const reopenTime = bdAccount.ReopenAt && bdAccount.ReopenAt !== '0' ? formatEpochDate(bdAccount.ReopenAt) : '';
        const linkedPrymaryWalletName = linkedWallets.find((linkedWallet) => linkedWallet.wallet.IsPrimary)?.['wallet']['AccountName'] || '';
        const linkedWalletsConcatName = linkedWallets.reduce((acc, linkedWallet, index) => {
            acc += ` ${linkedWallet.wallet.AccountName}${index !== linkedWallets.length - 1 ? ',' : ''}`;

            return acc;
        }, '');

        const cellValuesArray = [
            `"${firm.Name}"`,
            `"${bdAccount.AccountName}"`,
            `"${bdAccount.Account}"`,
            `"${linkedPrymaryWalletName}"`,
            `"${linkedWalletsConcatName}"`,
            `"${bdAccount.IsLocked}"`,
            `"${lastActivityTime}"`,
            `"${createTime}"`,
            `"${updateTime}"`,
            `"${modifiedByUser}"`,
            `"${closeTime}"`,
            `"${reopenTime}"\n`
        ].join(',');

        exportString += cellValuesArray;
    });

    download(`Lynq_Platform_${firm.Name}_BD_Accounts.csv`, exportString);
};

export const exportFirmMappedUserData = (firmId: string, features: IFeaturesContext, filter: FilterOption) => {
    const state = AdminStore.getState();
    const firm = state.firms[firmId];
    let users = getUsersByFirmId(state, undefined, undefined, undefined, firmId);

    const allUsers = Object.values(getUsers(state));
    const userRole = getCurrentRole(state);

    if (filter && filter.type === AgGridFilterType.Equal) {
        users = users.filter(user => user[filter.colId] === filter.filter);
    }

    let exportString = `Firm Name,Firm User,Username,User Email,User Phone Number,TokenAPI User,Frozen Status,Locked Status,Deleted Status,User Notes,Created At,Updated At,Updated By,Deleted At,Associated Account Name,Associated Account Address,Linked BD Account Number,Associated Account Access Control,Associated Account Email,User Account Role,Account ACG #1,Account ACG #2,Account ACG #3${userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN ? ',Last Logon' : ''}\n`;

    users.forEach(user => {
        const userName = `${user.FirstName} ${user.LastName}`;
        const mappedUserAccounts = getMappedUserAccountsByUserId(state, user.UserId, undefined, false, false);
        const updatedBy = allUsers.find(item => item.UserId === user.ModifiedBy);
        if (mappedUserAccounts && mappedUserAccounts.length > 0) {
            mappedUserAccounts.forEach(mappedUserAccount => {
                const role = (ClientWalletRolesMap.find(role => role.id === mappedUserAccount.userAccount.ClientRoleId) || {}).displayName;
                const ACG1 = (state.accountClientGroups[mappedUserAccount.account.ACGId1] || {}).ACGNumber || '-';
                const ACG2 = (state.accountClientGroups[mappedUserAccount.account.ACGId2] || {}).ACGNumber || '-';
                const ACG3 = (state.accountClientGroups[mappedUserAccount.account.ACGId3] || {}).ACGNumber || '-';

                exportString += `"${firm.Name}","${userName}","${user.Username}","${user.Email}",="${user.Phone}","${user.IsTokenAPIUser}","${user.IsFrozen}","${user.IsLocked}","${user.IsDeleted}","${user.Notes}","${formatEpochDate(user.CreatedAt)}","${formatEpochDate(user.UpdatedAt)}","${updatedBy ? updatedBy.Username : ''}","${user.IsDeleted ? formatEpochDate(user.UpdatedAt) : ''}","${mappedUserAccount.account.AccountName}","${mappedUserAccount.account.Account}","${obscureAccountNo(mappedUserAccount.linkedAccountNumber)}","${mappedUserAccount.account.AccessControl}","${mappedUserAccount.account.Email}","${role}","${ACG1}","${ACG2}","${ACG3}"${userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN && user.LastLogin && user.LastLogin !== '0' ? `,"${formatEpochDate(user.LastLogin)}"` : ''}\n`;
            });
        } else {
            exportString += `"${firm.Name}","${userName}","${user.Username}","${user.Email}",="${user.Phone}","${user.IsTokenAPIUser}","${user.IsFrozen}","${user.IsLocked}","${user.IsDeleted}","${user.Notes}","${formatEpochDate(user.CreatedAt)}","${formatEpochDate(user.UpdatedAt)}","","","","","","","","","","",${userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN && user.LastLogin && user.LastLogin !== '0' ? `,"${formatEpochDate(user.LastLogin)}"` : ''}\n`;
        }
    });

    download('Lynq_Platform_Firm_Users.csv', exportString);
};